import { CSSProperties } from 'react';
import { SvgIcon } from '@material-ui/core';
import { ReactComponent as DragAndDropIcon } from 'assets/images/drag-and-drop-icon.svg';
import SwitchSymphony from 'elements/SwitchSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import useBrand from 'Hooks/useBrand';

interface PlatformLinkOptionProps {
  // Core display properties
  alt: string;
  src: string;
  label: string;
  platformId: string;
  
  // Mode configuration
  mode?: 'simple' | 'full';
  
  // Platform toggle properties
  showToggle?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  
  // Auto-follow properties (full mode only)
  showAutofollow?: boolean;
  autofollowAllowed?: boolean;
  autofollowConnected?: boolean;
  onConnectClick?: (platformId: string) => void;
  
  // Custom link properties (full mode only)
  isCustomLink?: boolean;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
  
  // Styling
  className?: string;
  style?: CSSProperties;
}

const PlatformLinkOption = ({
  alt,
  src,
  label,
  platformId,
  mode = 'full',
  showToggle = true,
  checked = false,
  disabled = false,
  onChange,
  showAutofollow = false,
  autofollowAllowed = false,
  autofollowConnected = false,
  onConnectClick,
  isCustomLink = false,
  onEditClick,
  onDeleteClick,
  className = '',
  style = {},
}: PlatformLinkOptionProps) => {
  const { isPaidBrand } = useBrand();

  const renderAutofollowOptions = () => {
    if (mode !== 'full' || !isPaidBrand || isCustomLink || !showAutofollow) {
      return null;
    }
    
    let text;
    if (!autofollowAllowed) {
      text = <p className="text-gray-500 text-xs">This platform doesn't enable auto-following.</p>
    } else {
      if (autofollowConnected) {
        text = <p className="text-xs">⚡️ Fans who presave will auto-follow your profile.</p>
      } else {
        text = (
          <button 
            onClick={() => onConnectClick && onConnectClick(platformId)} 
            className="text-purple-500 text-xs my-0"
          >
            Connect {label} to enable autofollows.
          </button>
        )
      }
    }

    return (
      <div className="mt-0.25">
        {text}
      </div>
    )
  }

  const renderCustomLinkActions = () => {
    if (mode !== 'full' || !isCustomLink) return null;

    return (
      <div className="flex gap-2 ml-auto">
        <ButtonSymphony
          variant="text"
          color="red"
          onClick={onEditClick}
          className="editCustomLinkButton"
        >
          Edit
        </ButtonSymphony>
        <ButtonSymphony
          variant="text"
          onClick={onDeleteClick}
          className="deleteCustomLinkButton"
        >
          Delete
        </ButtonSymphony>
      </div>
    );
  }

  const renderToggleSwitch = () => {
    if (!showToggle) return null;

    return (
      <SwitchSymphony
        className={mode === 'simple' ? 'platformSwitch' : 'detailsTabSwitchMusicPlatforms'}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ marginLeft: 'auto' }}
      />
    );
  }

  // Different layouts for different modes
  if (mode === 'simple') {
    // Simple mode for EditPlatformsModal - uses the original layout
    return (
      <div 
        className={`flex flex-row items-center justify-between border border-gray-200 rounded-xl w-full p-4 gap-4 ${className}`}
        style={style}
      >
        <div>
          <SvgIcon viewBox="0 0 32 46">
            <DragAndDropIcon />
          </SvgIcon>
        </div>
        <div className="flex flex-row items-center w-full gap-4">
          <div>
            <img className="w-10 h-10" alt={alt} src={src} />
          </div>
          <div className="text-gray-500 w-full">Pre-save on {label}</div>
          {renderToggleSwitch()}
        </div>
      </div>
    );
  }

  // Full mode for DetailsTab - more comprehensive layout
  return (
    <div 
      className={`flex p-4 align-middle items-center gap-4 border w-full ${className}`}
      style={style}
    >
      <div>
        <SvgIcon viewBox="0 0 32 46">
          <DragAndDropIcon />
        </SvgIcon>
      </div>

      <div className="flex items-center gap-4">
        {src ? (
          <img className="w-10 h-10" alt={alt} src={src} />
        ) : (
          // Generic link icon for custom links without selected platform icon
          <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-gray-600 text-sm">🔗</span>
          </div>
        )}
        <div className="flex flex-col align-start">
          <div>{label}</div>
          {renderAutofollowOptions()}
        </div>
      </div>

      {isCustomLink ? (
        renderCustomLinkActions()
      ) : (
        renderToggleSwitch()
      )}
    </div>
  );
};

export default PlatformLinkOption; 